import { type Decimal } from '@capsicum/common';
import { AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { match } from 'ts-pattern';

import { fanTokenOfferingAPIErrorModalAtom } from '../data-access';
import { FTOErrorCodes, type FTOErrorModalParams } from '../models';

import { useGetErrorMessagesForFTO } from './useGetErrorMessagesForFTO';
import { usePurchaseFTODataAtom } from './usePurchaseFTODataAtom';

/**
 * Component that handle specific error code's returned by endpoints.
 * - Sold out
 * - Too many request
 * - Amount not available anymore
 *
 * @returns {void} function that will handle error object sent back by query.
 */
export const useCustomErrorManagementForFanTokenOffering = (minAmount?: Decimal) => {
	const { fanTokenShortCode } = usePurchaseFTODataAtom();
	const setFanTokenOfferingAPIErrorModal = useSetAtom(fanTokenOfferingAPIErrorModalAtom);

	const errorMessages = useGetErrorMessagesForFTO(fanTokenShortCode, minAmount);

	const getErrorCode = <EType extends Error>(error: EType): FTOErrorCodes => {
		if (error instanceof AxiosError) {
			return error.response?.data?.errors[0].code;
		}

		return FTOErrorCodes.DefaultError;
	};

	// If the return is true, it will stop the propagation and not execute the common error interceptor
	const manageFanTokenOfferingInterceptor = <EType extends Error>(error: EType): boolean => {
		const errorCode: FTOErrorCodes = getErrorCode(error);

		// display modal associated to code, or default if code is not a known code
		// AmountNotAvailable, and AmountNotAvailable2 display the same message.
		setFanTokenOfferingAPIErrorModal({
			data: match(errorCode)
				.returnType<FTOErrorModalParams>()
				.with(FTOErrorCodes.SoldOut, FTOErrorCodes.NotOpen, () => errorMessages[FTOErrorCodes.SoldOut])
				.with(FTOErrorCodes.TooManyRequests, () => errorMessages[FTOErrorCodes.TooManyRequests])
				.with(FTOErrorCodes.AmountNotAvailable, () => errorMessages[FTOErrorCodes.AmountNotAvailable])
				.otherwise(() => errorMessages[FTOErrorCodes.DefaultError]),
			visible: true
		});

		return true;
	};

	return {
		manageFanTokenOfferingInterceptor
	};
};
